<template>
  <div class="flex">
    <div
      class="mx-auto w-full max-w-91.25 md:max-w-113 xl:max-w-137.5 2xl:max-w-160.25"
    >
      <div
        class="relative mb-5 flex w-full items-center justify-center gap-8.75 md:mb-7.5 xl:mb-7.5 2xl:mb-11.25"
      >
        <SectionTitle
          :title="sliderTitle"
          :link="link ? link : undefined"
          :target="link ? target : undefined"
        />

        <!-- Pagination & Navigation -->
        <div
          v-if="slides.length > 1"
          class="group flex items-center justify-start gap-4 xl:min-w-20"
        >
          <span
            class="transform-colors text-lg leading-none font-normal duration-100 ease-out group-hover:text-[#595959] xl:text-xl xl:leading-[120%]"
          >
            {{ currentIndex + 1 }} / {{ slides.length }}
          </span>

          <button
            @click="nextSlide"
            :aria-label="t('common.next.slide')"
            class="transform-colors relative z-10 flex h-6.5 w-6.5 cursor-pointer items-center justify-center rounded-full bg-black text-white transition-opacity duration-100 ease-out group-hover:bg-[#595959] hover:opacity-80"
          >
            <ArrowIcon class="h-3 w-3" />
          </button>
        </div>
      </div>

      <!-- Swiper Slider Wrapper -->
      <Swiper
        :slides-per-view="1"
        :loop="slides?.length > 1"
        :allow-touch-move="false"
        @swiper="onSwiperInit"
        @slideChange="onSlideChange"
        class="-mt-4 h-full w-full ![clip-path:inset(-100px_0px)]"
      >
        <SwiperSlide v-for="(slide, index) in slides" :key="slide.id || index">
          <NuxtLink
            v-if="slide?.menu_url"
            :to="localePath(slide?.menu_url)"
            class="group mx-auto flex w-full flex-col items-center gap-5 pt-4 pb-2 md:gap-7.5 xl:gap-10"
          >
            <div
              class="aspect-2/3 w-full max-w-[72%] p-2 md:max-w-[73%] lg:max-w-[83%] xl:h-157.5 xl:max-w-[76.5%] 2xl:h-183.5"
            >
              <NuxtImg
                v-if="slide?.image?.url"
                :src="slide.image.url"
                :alt="slide?.image?.alt_text || slide.title + slide.id || ''"
                format="webp"
                sizes="xs:230px sm:300px md:350px lg:400px xl:450px xxl:500px"
                class="mx-auto h-full w-full object-cover shadow-[0_5.754px_23.016px_rgba(0,0,0,0.20)] transition-transform duration-100 ease-out group-hover:scale-[1.02] sm:shadow-[0_6.269px_25.077px_rgba(0,0,0,0.20)] md:shadow-[0_7.81px_31.238px_rgba(0,0,0,0.20)] lg:shadow-[0_8.968px_35.873px_rgba(0,0,0,0.20)] min-[1440px]:shadow-[0_10px_40px_rgba(0,0,0,0.20)] min-[1680px]:shadow-[0_11.667px_46.667px_rgba(0,0,0,0.20)]"
                loading="lazy"
              />
              <NuxtImg
                v-else
                src="https://placehold.co/600x900"
                :alt="slide.title + slide.id || ''"
                format="webp"
                sizes="xs:230px sm:300px md:350px lg:400px xl:450px xxl:500px"
                class="mx-auto h-full w-full object-cover shadow-[0_5.754px_23.016px_rgba(0,0,0,0.20)] transition-transform duration-100 ease-out group-hover:scale-[1.02] sm:shadow-[0_6.269px_25.077px_rgba(0,0,0,0.20)] md:shadow-[0_7.81px_31.238px_rgba(0,0,0,0.20)] lg:shadow-[0_8.968px_35.873px_rgba(0,0,0,0.20)] min-[1440px]:shadow-[0_10px_40px_rgba(0,0,0,0.20)] min-[1680px]:shadow-[0_11.667px_46.667px_rgba(0,0,0,0.20)]"
                loading="lazy"
              />
            </div>

            <h2
              class="w-69.25 max-w-full text-center leading-[120%] md:w-98.5 md:text-[17px] lg:w-112.5 xl:w-137.5! xl:text-xl 2xl:w-160.25! 2xl:text-[23px]/[29px]"
            >
              {{ slide.title }}
            </h2>
          </NuxtLink>

          <div
            v-else
            class="group mx-auto flex w-full flex-col items-center gap-5 pt-4 pb-2 md:gap-7.5 xl:gap-10"
          >
            <div
              class="aspect-2/3 w-full max-w-[72%] md:max-w-[73%] lg:max-w-[83%] xl:h-157.5 xl:max-w-[76.5%] 2xl:h-183.5"
            >
              <NuxtImg
                v-if="slide?.image?.url"
                :src="slide.image.url"
                :alt="slide?.image?.alt_text || slide.title + slide.id || ''"
                format="webp"
                sizes="xs:230px sm:300px md:350px lg:400px xl:450px xxl:500px"
                class="mx-auto h-full w-full object-cover shadow-[0_5.754px_23.016px_rgba(0,0,0,0.20)] transition-transform duration-100 ease-out group-hover:scale-[1.02] sm:shadow-[0_6.269px_25.077px_rgba(0,0,0,0.20)] md:shadow-[0_7.81px_31.238px_rgba(0,0,0,0.20)] lg:shadow-[0_8.968px_35.873px_rgba(0,0,0,0.20)] min-[1440px]:shadow-[0_10px_40px_rgba(0,0,0,0.20)] min-[1680px]:shadow-[0_11.667px_46.667px_rgba(0,0,0,0.20)]"
                loading="lazy"
              />
              <NuxtImg
                v-else
                src="https://placehold.co/600x900"
                :alt="slide.title + slide.id || ''"
                format="webp"
                sizes="xs:230px sm:300px md:350px lg:400px xl:450px xxl:500px"
                class="mx-auto h-full w-full object-cover shadow-[0_5.754px_23.016px_rgba(0,0,0,0.20)] transition-transform duration-100 ease-out group-hover:scale-[1.02] sm:shadow-[0_6.269px_25.077px_rgba(0,0,0,0.20)] md:shadow-[0_7.81px_31.238px_rgba(0,0,0,0.20)] lg:shadow-[0_8.968px_35.873px_rgba(0,0,0,0.20)] min-[1440px]:shadow-[0_10px_40px_rgba(0,0,0,0.20)] min-[1680px]:shadow-[0_11.667px_46.667px_rgba(0,0,0,0.20)]"
                loading="lazy"
              />
            </div>

            <h2
              class="w-69.25 max-w-full text-center leading-[120%] md:w-98.5 md:text-[17px] lg:w-112.5 xl:w-137.5! xl:text-xl 2xl:w-160.25! 2xl:text-[23px]/[29px]"
            >
              {{ slide.title }}
            </h2>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import type { Swiper as SwiperClass } from 'swiper/types'
import type { Publication } from '~/types'

interface Props {
  slides: Publication[]
  sliderTitle: string
  link?: string
  target?: string
}

const props = defineProps<Props>()
const localePath = useLocalePath()
const { locale, t } = useI18n()

const swiperInstance = ref<SwiperClass | null>(null)
const currentIndex = ref(0)

const onSwiperInit = (swiper: SwiperClass) => {
  swiperInstance.value = swiper
}

const onSlideChange = (swiper: SwiperClass) => {
  currentIndex.value = swiper.realIndex
}

const nextSlide = () => {
  if (swiperInstance.value) {
    swiperInstance.value.slideNext()
  }
}
</script>
